import { useEffect, useRef, useState } from "react";
import { _post } from "../services/apiClient";
import { apiUrls } from "../config/apiUrls.const";
import { showAlert, showToast } from "../services/alertService";
import { getDecodedToken, resetUserData } from "../services/authService";
import { setSecureItem } from "../utils/LocalStorageManager";
import LocationSelector from "./components/LocationSelector";
import { Link } from "lucide-react";
import FileInput from "./components/FileInput";
import LocationPickerMap, { LocationPickerRef } from "./components/LocationPickerMap";
import { useUser } from "../contexts/UserContext";

type ValidationErrors = {
  [key: string]: string[];
};

type LatLng = {
  lat: number;
  lng: number;
};

export default function AddBusiness() {
  const { user, logout } = useUser();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [businessNameExists, setBusinessNameExists] = useState(false);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    businessName: "",
    logo: "" as File | string,
    mobileNo: "",
    email: "",
    address: "",
    location: null as LatLng | null,

    map_image: null as File | null, // ✅ ADD THIS

    division_id: "",
    district_id: "",
    thana_id: "",
    postal_code: "",
    agreement: false,
  });

  // 🔹 Generic input handler
  const handleInputChange =
    (field: keyof typeof formData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value =
                    e.target.type === "checkbox"
                        ? (e.target as HTMLInputElement).checked
                        : e.target.value;
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => {
          const { [field]: _, ...rest } = prev;
          return rest;
        });
      }
    };

  // 🔹 Location selector handler
  const handleLocationChange = (name: string, value: string) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // 🔹 Check business name
  const checkBusinessName = async (): Promise<boolean> => {
    if (!formData.businessName.trim()) return false;

    try {
      const response = await _post(apiUrls.checkBusinessName, {
        name: formData.businessName,
      });
      const exists = response.data?.data;
      setBusinessNameExists(!!exists);
      return !!exists;
    } catch {
      return false;
    }
  };

  const handleLogoChange = (name: string, files: FileList | null) => {
      if (files && files.length > 0) {
          const file = files[0];
          setFormData((prev) => ({ ...prev, logo: file }));
          setLogoPreview(URL.createObjectURL(file));
      }
  };

  const mapRef = useRef<LocationPickerRef>(null);

  const submitCreate = async () => {
    const mapImage = await mapRef.current?.generateStaticImage();

    const fd = new FormData();
    if (formData.location) {
      fd.append("lat", formData.location.lat.toString());
      fd.append("lng", formData.location.lng.toString());
    }

    if (mapImage) {
      fd.append("map_image", mapImage);
    }

    await fetch("/api/business/store", {
      method: "POST",
      body: fd,
    });
  };

  // 🔹 Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    // Validation definitions matching all required inputs
    const newErrors: ValidationErrors = {};
    if (!formData.businessName.trim()) newErrors.businessName = ["Business Name is required"];
    if (!formData.mobileNo.trim()) newErrors.mobileNo = ["Mobile number is required"];
    if (!formData.address.trim()) newErrors.address = ["Address is required"];
    if (!formData.location) newErrors.location = ["Map location is required!"];
    if (!formData.division_id) newErrors.division_id = ["Division selection is required"];
    if (!formData.district_id) newErrors.district_id = ["District selection is required"];
    if (!formData.thana_id) newErrors.thana_id = ["Thana selection is required"];
    if (!formData.postal_code.trim()) newErrors.postal_code = ["Postal code is required"];
    
    // ✅ ADDED VALIDATION FOR THE AGREEMENT CHECKBOX
    if (!formData.agreement) {
      newErrors.agreement = ["You must accept the terms and conditions to proceed"];
    }

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    try {
      let mapImage = formData.map_image;

      if (!mapImage && mapRef.current) {
        mapImage = await mapRef.current.generateStaticImage();
        if (mapImage) {
          setFormData((p) => ({ ...p, map_image: mapImage }));
        }
      }

      const reqData = new FormData();
      reqData.append("name", formData.businessName);
      reqData.append("mobile_no", formData.mobileNo);
      reqData.append("email", formData.email);
      reqData.append("address", formData.address);
      reqData.append("division_id", formData.division_id);
      reqData.append("district_id", formData.district_id);
      reqData.append("thana_id", formData.thana_id);
      reqData.append("postal_code", formData.postal_code);

      if (formData.logo instanceof File) {
        reqData.append("logo", formData.logo);
      }

      if (formData.location) {
        reqData.append("lat", formData.location.lat.toString());
        reqData.append("lon", formData.location.lng.toString());
      }

      if (mapImage) {
        reqData.append("map_image", mapImage);
      }

      const res = await _post(apiUrls.storeBusiness, reqData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if(res.data?.success){
        showToast({
          icon: "success",
          title: "Success",
          text: "Business registered successfully!",
        });

        const checkUserData = await resetUserData();
        if(checkUserData?.data?.success){
          window.location.href = "/profile";
        }
      }

    } catch (err: any) {
      showToast({
        icon: "error",
        title: "Business Error",
        text: err.message || "Registration failed",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <h1 className="page-header">Add Business</h1>
      <hr className="section-devider" />

      <form onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-8">
            <div className="row">
              <div className="col-sm-6">
                <div className="mb-3">
                  <label>Business Name</label>
                  <input
                    className={`form-control ${errors.businessName ? "is-invalid" : ""}`}
                    value={formData.businessName}
                    onChange={handleInputChange("businessName")}
                    onBlur={checkBusinessName}
                  />
                  {businessNameExists && (
                    <div className="text-danger">Business name already exists</div>
                  )}
                </div>
              </div>
              <div className="col-sm-6">
                <div className="mb-3">
                  <FileInput
                      label="Business Logo"
                      name="logo"
                      multiple={false}
                      existing={logoPreview}
                      onChange={handleLogoChange}
                  />
                </div>
              </div>

              <div className="col-sm-6">
                <div className="mb-3">
                  <label>Mobile</label>
                  <input
                    className={`form-control ${errors.mobileNo ? "is-invalid" : ""}`}
                    value={formData.mobileNo}
                    onChange={handleInputChange("mobileNo")}
                  />
                  {errors.mobileNo && (
                    <div className="invalid-feedback">{errors.mobileNo[0]}</div>
                  )}
                </div>
              </div>
              
              <div className="col-sm-6">
                <div className="mb-3">
                  <label>Email</label>
                  <input
                    className="form-control"
                    value={formData.email}
                    onChange={handleInputChange("email")}
                  />
                </div>
              </div>

              <div className="col-md-12">
                <div className="mb-3">
                  <label>Address</label>
                  <input
                    className={`form-control ${errors.address ? "is-invalid" : ""}`}
                    value={formData.address}
                    onChange={handleInputChange("address")}
                  />
                </div>
              </div>

              <div className="mb-3">
  {/* 🔹 LOCATION SELECTOR */}
  <div className={errors.division_id || errors.district_id || errors.thana_id || errors.postal_code ? "is-invalid" : ""}>
    <LocationSelector
      setClass="col-md-3"
      division_id={formData.division_id}
      district_id={formData.district_id}
      thana_id={formData.thana_id}
      postal_code={formData.postal_code}
      required={true}
      onChange={handleLocationChange}
    />
  </div>
  {(errors.division_id || errors.district_id || errors.thana_id || errors.postal_code) && (
    <div className="text-danger small mt-1">
      {errors.division_id?.[0] || errors.district_id?.[0] || errors.thana_id?.[0] || errors.postal_code?.[0]}
    </div>
  )}
</div>
            </div>
          </div>

          <div className="col-md-4">
  <label className="form-label fw-semibold">Pick Location</label>
  
  {/* Apply conditional borders or backgrounds to the container wrapper when an error exists */}
  <div 
    className={`map-container mb-2 rounded overflow-hidden border ${
      errors.location ? "border-danger shadow-sm" : ""
    }`}
    style={{ transition: "border-color 0.15s ease-in-out" }}
  >
    <LocationPickerMap
      ref={mapRef}
      mode="create"
      value={formData.location}
      onChange={(loc) =>
        setFormData((p) => ({ ...p, location: loc }))
      }
    />
  </div>
  
  {/* Render the error block clearly with a small location icon indicator */}
  {errors.location && (
    <div className="text-danger small fw-medium mt-1 d-flex align-items-center">
      <span className="me-1">⚠️</span> {errors.location[0]}
    </div>
  )}
</div>

          <div className="col-12 mb-3">
  <div className="form-check">
      <input
          type="checkbox"
          id="agreement"
          className={`form-check-input ${errors.agreement ? "is-invalid" : ""}`}
          checked={formData.agreement}
          onChange={handleInputChange("agreement")}
      />
      <label htmlFor="agreement" className="form-check-label">
          I agree to the <a href="/terms" target="_blank" className="text-primary">terms and conditions</a>
      </label>
      {errors.agreement && (
        <div className="invalid-feedback">{errors.agreement[0]}</div>
      )}
  </div>
</div>

          <div className="col-12">
            <button type="submit" className="btn btn-primary w-100" disabled={loading}>
              {loading ? "Registering..." : "Register Business"}
            </button>
          </div>
        </div>
      </form>
    </>
  );
}
